import React, { useState, useEffect, useRef } from 'react'
import './Testimonials.css'

const API = process.env.REACT_APP_API_URL || 'https://api.rasteghub.co.ke'

function initials(name) {
  return name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'
}

// ── Star rating component ─────────────────────────────────────────
function StarRating({ value, onChange, readonly = false }) {
  const [hover, setHover] = useState(0)
  return (
    <div className='testi-stars' aria-label={`Rating: ${value} of 5`}>
      {[1,2,3,4,5].map(star => (
        <button
          key={star}
          type='button'
          className={`testi-star ${(hover || value) >= star ? 'testi-star--on' : ''}`}
          onClick={() => !readonly && onChange?.(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          disabled={readonly}
          aria-label={`${star} star`}
        >★</button>
      ))}
    </div>
  )
}

// ── Review form modal ─────────────────────────────────────────────
function ReviewModal({ onClose }) {
  const [form, setForm] = useState({
    name: '', role: '', company: '',
    review: '', project_link: '', project_label: '', rating: 5,
  })
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone]             = useState(false)
  const [error, setError]           = useState(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const submit = async () => {
    if (!form.name.trim() || !form.review.trim()) {
      setError('Your name and review are required.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res  = await fetch(`${API}/api/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          role: form.role || null,
          company: form.company || null,
          review: form.review,
          project_link: form.project_link || null,
          project_label: form.project_label || form.company || null,
          rating: form.rating,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Submission failed.')
      setDone(true)
    } catch(e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className='rm-overlay' onClick={onClose}>
      <div className='rm-modal' onClick={e => e.stopPropagation()}>

        <div className='rm-header'>
          <div>
            <h2>{done ? 'Thank you! 🎉' : 'Leave a Review'}</h2>
            {!done && <p>Share your experience working with RastegHub</p>}
          </div>
          <button className='rm-close' onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {done ? (
          <div className='rm-success'>
            <div className='rm-success-orb'></div>
            <span className='rm-success-icon'>✓</span>
            <p>Your review has been submitted and will appear on the site once approved. We really appreciate you taking the time!</p>
            <button className='rm-btn-primary' onClick={onClose}>Close</button>
          </div>
        ) : (
          <div className='rm-body'>
            {/* Rating */}
            <div className='rm-field rm-field--center'>
              <label>Your Rating <span>*</span></label>
              <StarRating value={form.rating} onChange={v => set('rating', v)} />
            </div>

            {/* Name + Role */}
            <div className='rm-row'>
              <div className='rm-field'>
                <label>Full Name <span>*</span></label>
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder='Jane Doe' />
              </div>
              <div className='rm-field'>
                <label>Your Role <span className='opt'>(optional)</span></label>
                <input value={form.role} onChange={e => set('role', e.target.value)} placeholder='CEO, Designer…' />
              </div>
            </div>

            {/* Company */}
            <div className='rm-field'>
              <label>Company / Organisation <span className='opt'>(optional)</span></label>
              <input value={form.company} onChange={e => set('company', e.target.value)} placeholder='Acme Inc.' />
            </div>

            {/* Review */}
            <div className='rm-field'>
              <label>Your Review <span>*</span></label>
              <textarea
                rows={4}
                value={form.review}
                onChange={e => set('review', e.target.value)}
                placeholder='Tell others about your experience working with us…'
              />
            </div>

            {/* Project link */}
            <div className='rm-row'>
              <div className='rm-field'>
                <label>Project URL <span className='opt'>(optional)</span></label>
                <input value={form.project_link} onChange={e => set('project_link', e.target.value)} placeholder='https://yourproject.com' />
              </div>
              <div className='rm-field'>
                <label>Link Label <span className='opt'>(optional)</span></label>
                <input value={form.project_label} onChange={e => set('project_label', e.target.value)} placeholder='View Project' />
              </div>
            </div>

            {error && <p className='rm-error'>⚠ {error}</p>}

            <div className='rm-actions'>
              <button className='rm-btn-cancel' onClick={onClose}>Cancel</button>
              <button className='rm-btn-primary' onClick={submit} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Review'}
              </button>
            </div>

            <p className='rm-disclaimer'>
              Reviews are reviewed before being published. We don't edit your words — only screen for spam.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Single testimonial card ───────────────────────────────────────
function TestiCard({ t, active, onClick }) {
  return (
    <div className={`testi-card ${active ? 'testi-card--active' : ''}`} onClick={onClick}>
      <span className='testi-quote-mark'>"</span>
      <p className='testi-review'>{t.review}</p>
      <div className='testi-card-footer'>
        <div className='testi-person'>
          <div className='testi-avatar'>{initials(t.name)}</div>
          <div className='testi-meta'>
            <strong className='testi-name'>{t.name}</strong>
            <span className='testi-role'>
              {[t.role, t.company].filter(Boolean).join(', ')}
            </span>
            {t.rating && (
              <div className='testi-rating-display'>
                <StarRating value={t.rating} readonly />
              </div>
            )}
          </div>
        </div>
        {t.project_link && active && (
          <a
            className='testi-project-link'
            href={t.project_link}
            target='_blank'
            rel='noreferrer'
            onClick={e => e.stopPropagation()}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            <span>{t.project_label || 'View Project'}</span>
          </a>
        )}
      </div>
    </div>
  )
}

// ── No reviews fallback ───────────────────────────────────────────
function NoReviews({ onWrite }) {
  return (
    <div className='testi-empty'>
      <div className='testi-empty-orb' aria-hidden='true'></div>
      <span className='testi-empty-icon' aria-hidden='true'>✦</span>
      <h3 className='testi-empty-heading'>No reviews yet — but that's about to change.</h3>
      <p className='testi-empty-body'>
        We're early, we're hungry, and we do work worth talking about.<br />
        Partner with us, and you'll be the first voice others trust.
      </p>
      <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        <a href='#contact' className='testi-empty-cta'>
          Get in touch →
        </a>
        <button className='testi-write-btn' onClick={onWrite}>
          ✏ Write a Review
        </button>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────
export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([])
  const [loading, setLoading]           = useState(true)
  const [active, setActive]             = useState(0)
  const [modalOpen, setModal]           = useState(false)
  const trackRef                        = useRef(null)

  useEffect(() => {
    fetch(`${API}/api/testimonials`)
      .then(r => r.json())
      .then(d => setTestimonials(d.testimonials || []))
      .catch(() => setTestimonials([]))
      .finally(() => setLoading(false))
  }, [])

  const goTo = (index) => {
    setActive(index)
    const card = trackRef.current?.children[index]
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }

  const prev = () => goTo(Math.max(0, active - 1))
  const next = () => goTo(Math.min(testimonials.length - 1, active + 1))

  return (
    <section className='testimonials' id='testimonials'>
      <div className='testi-grid-bg' aria-hidden='true'></div>

      <div className='testi-header'>
        <span className='testi-eyebrow'>Client Stories</span>
        <h2 className='testi-title'>
          Trusted by builders,<br />
          <em>backed by results.</em>
        </h2>
      </div>

      {loading && (
        <div className='testi-empty'>
          <div className='testi-spinner'></div>
        </div>
      )}

      {!loading && testimonials.length === 0 && (
        <NoReviews onWrite={() => setModal(true)} />
      )}

      {!loading && testimonials.length > 0 && (
        <>
          <div className='testi-track-wrapper'>
            <div className='testi-track' ref={trackRef}>
              {testimonials.map((t, i) => (
                <TestiCard key={t.id} t={t} active={i === active} onClick={() => goTo(i)} />
              ))}
            </div>
          </div>

          <div className='testi-controls'>
            <button className='testi-arrow' onClick={prev} disabled={active === 0}>←</button>
            <div className='testi-dots'>
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`testi-dot ${i === active ? 'testi-dot--active' : ''}`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
            <button className='testi-arrow' onClick={next} disabled={active === testimonials.length - 1}>→</button>
          </div>

          <p className='testi-counter'>
            <span>{String(active + 1).padStart(2, '0')}</span> / {String(testimonials.length).padStart(2, '0')}
          </p>

          {/* Write review CTA */}
          <div className='testi-write-row'>
            <button className='testi-write-btn' onClick={() => setModal(true)}>
              ✏ &nbsp;Leave a Review
            </button>
          </div>
        </>
      )}

      {modalOpen && <ReviewModal onClose={() => setModal(false)} />}
    </section>
  )
}